import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
    FormHeader,
    PARTIAL_INTERVAL_FIELD_CONFIG,
    PARTIAL_INTERVAL_WASH,
    buildRateDefaultValues,
    formatLockDate,
    partialIntervalMethodValue,
} from "./BillingRateBuilderShared";
import RateEditBody from "./BillingRateBuilderRateEditBody";
import CategoryRateEditBody from "./BillingRateBuilderCategoryRateEditBody";
import { buildRateCategorySliceSchema, rateHeaderSchema } from "./billingRateBuilderSchemas";
import { BILLING_CATEGORY, RATE_CREATE_REASON } from "../../utils/billingRateUtils";

const buildPayloadFromValues = (values, originalPayload) => {
    const isNew = !originalPayload;
    const accountOption = values.account;
    const accountHccId = accountOption?.value ?? "";
    const accountName =
        accountOption?.additional?.accountName ??
        (typeof accountOption?.label === "string" ? accountOption.label : null) ??
        null;
    // The benefit plan name lives on rate.benefitPlan, while the top-level
    // payload.benefitPlan holds the HCC ID (the key used to resolve coverage tier
    // headers / totals). Keep them distinct so totals continue to resolve.
    const benefitPlanName = values.benefitPlan?.value ?? "";
    const benefitPlanHccId =
        values.benefitPlan?.additional?.benefitPlanHccId ??
        originalPayload?.benefitPlan ??
        "";

    // Build billingRate explicitly in the required key order instead of spreading the
    // original, so the field order can never depend on the source object's shape.
    const updatedBillingRate = {
        billingCategoryRates: (values.billingCategoryRates ?? []).map((cat) => {
            const method = partialIntervalMethodValue(cat.partialIntervalCalculationMethod);
            const activeFieldName = method
                ? PARTIAL_INTERVAL_FIELD_CONFIG[method]?.name
                : null;
            const rawActiveValue = activeFieldName ? cat[activeFieldName] : undefined;
            // The active interval field is disabled in the UI, so react-hook-form
            // reports it as undefined on submit. Wash day must always be 15; other
            // methods fall back to 1 when empty.
            const activeValue =
                method === PARTIAL_INTERVAL_WASH
                    ? 15
                    : rawActiveValue === "" || rawActiveValue == null
                        ? 1
                        : rawActiveValue;
            return {
                partialIntervalCalculationMethod: method,
                ...(activeFieldName ? { [activeFieldName]: activeValue } : {}),
                billingPartyPercentages: (cat.billingPartyPercentages ?? []).map((b) => ({
                    billingParty: b?.billingParty ?? "",
                    billPercentage: b?.billPercentage ?? "",
                })),
                billingRateDefinitions: (cat.billingRateDefinitions ?? []).map((d) => ({
                    componentReference: {
                        lockDate: formatLockDate(d?.componentReference?.lockDate),
                        component: d?.componentReference?.component?.value ?? "",
                    },
                })),
                billingCategory: {
                    codeSetName: BILLING_CATEGORY.codeSetName,
                    codeEntry: cat.billingCategory?.value ?? "",
                },
            };
        }),
        benefitPlan: benefitPlanName,
        account: accountHccId,
    };

    // shortName is kept off the submitted payload's billingCategory and stored on
    // the wrapper as a codeEntry -> shortName dictionary instead.
    const billingCategoryShortNames = {};
    (values.billingCategoryRates ?? []).forEach((cat) => {
        const codeEntry = cat.billingCategory?.value ?? "";
        const shortName = cat.billingCategory?.additional?.shortName ?? "";
        if (codeEntry && shortName) billingCategoryShortNames[codeEntry] = shortName;
    });

    // Construct billingRatePayload explicitly in the required key order
    // (asOfDate, maintenanceInfo, maintenanceReasonCode, maintenanceComment,
    // billingRate). For existing payloads the maintenance fields are preserved
    // from the original; for new ones the Create defaults are used. Building it
    // explicitly avoids depending on the spread source's key order.
    const originalBrp = originalPayload?.billingRatePayload ?? {};
    const billingRatePayload = {
        ...(originalBrp.asOfDate !== undefined ? { asOfDate: originalBrp.asOfDate } : {}),
        maintenanceInfo: isNew
            ? { transactionType: "Create", overrideAction: false }
            : originalBrp.maintenanceInfo,
        maintenanceReasonCode: isNew ? RATE_CREATE_REASON : originalBrp.maintenanceReasonCode,
        maintenanceComment: isNew ? values.description : originalBrp.maintenanceComment,
        billingRate: updatedBillingRate,
    };

    // The wrapper's own key order isn't part of the submitted payload shape, so
    // spreading the original here is safe.
    return {
        ...originalPayload,
        account: accountHccId,
        accountName,
        benefitPlan: benefitPlanHccId,
        billingCategoryShortNames,
        billingRatePayload,
    };
};

const RateEditForm = ({
    initial,
    resolvedAccountRow,
    componentsByName,
    billingCategoryRows,
    defaultLockDate,
    onBack,
    onSave,
}) => {
    // Ref keeps the resolver callback up-to-date with whichever view is active
    // without changing the resolver's function identity (which would re-init the form).
    const categoryEditIndexRef = useRef(null);

    const resolver = useCallback((values, context, options) => {
        const idx = categoryEditIndexRef.current;
        return idx != null
            ? zodResolver(buildRateCategorySliceSchema(idx))(values, context, options)
            : zodResolver(rateHeaderSchema)(values, context, options);
    }, []);

    const defaultValues = useMemo(
        () => buildRateDefaultValues(initial, resolvedAccountRow, billingCategoryRows),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [],
    );
    const methods = useForm({
        mode: "all",
        resolver,
        defaultValues,
    });
    const [categoryEditIndex, setCategoryEditIndex] = useState(null);
    const [isAddingCategory, setIsAddingCategory] = useState(false);
    // Snapshot of the category values captured when entering edit mode.
    // Restored when the user clicks Back (cancel) instead of Save.
    const [categorySnapshot, setCategorySnapshot] = useState(null);
    // Holds the useFieldArray `remove` function registered by RateEditBody.
    const removeCategoryFnRef = useRef(null);

    // Sync ref during render so the resolver always reads the current view.
    categoryEditIndexRef.current = categoryEditIndex;

    const isAdding = !initial;
    const { isValid } = methods.formState;

    const isFirstRenderRef = useRef(true);
    useEffect(() => {
        if (isFirstRenderRef.current) {
            isFirstRenderRef.current = false;
            // For existing payloads, run validation on mount so errors are
            // visible immediately.
            if (!isAdding) methods.trigger();
            return;
        }
        // When the active view switches, re-validate so
        // isValid reflects whichever schema is now in use.
        methods.trigger();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [categoryEditIndex]);

    const handleEditCategory = (index, isNew = false) => {
        // Deep-clone the current values before editing so Back can restore them.
        // getValues() returns the live object reference inside RHF's store; without
        // cloning, mutations made during editing would silently overwrite the snapshot.
        if (!isNew) {
            setCategorySnapshot(structuredClone(methods.getValues(`billingCategoryRates.${index}`)));
        }
        setCategoryEditIndex(index);
        setIsAddingCategory(Boolean(isNew));
    };

    // Pressing Back (cancel) while in category-edit view discards changes.
    const handleCategoryBack = () => {
        if (isAddingCategory) {
            // The blank category was already appended by RateEditBody before
            // navigating here; remove it to undo the append.
            removeCategoryFnRef.current?.(categoryEditIndex);
        } else if (categorySnapshot != null) {
            // Restore the snapshot, reverting any edits the user made.
            methods.setValue(
                `billingCategoryRates.${categoryEditIndex}`,
                categorySnapshot,
                { shouldValidate: false },
            );
        }
        setCategorySnapshot(null);
        setCategoryEditIndex(null);
        setIsAddingCategory(false);
    };

    // Save always submits the full rate payload regardless of which view is active.
    // The context-aware resolver ensures the Save button is only enabled when
    // the currently visible section (rate header or category) is valid.
    //
    // We read the complete form state via getValues() rather than relying on the
    // values handleSubmit passes in: those are the resolver-parsed values, and the
    // active Zod schema (e.g. the category-slice schema) strips keys it doesn't
    // declare (account, benefitPlan, description), which would drop them from the payload.
    const handleSave = methods.handleSubmit(() => {
        onSave(buildPayloadFromValues(methods.getValues(), initial));
    });

    const handleBack =
        categoryEditIndex == null
            ? (isAdding ? null : onBack)
            : handleCategoryBack;

    const showCreateLabel =
        (isAdding && categoryEditIndex == null) ||
        (categoryEditIndex != null && isAddingCategory);

    return (
        <FormProvider {...methods}>
            <FormHeader
                onBack={handleBack}
                onSave={handleSave}
                saveLabel={showCreateLabel ? "Create" : "Save"}
                noBackTitle={
                    isAdding && categoryEditIndex == null ? "Create New Rate" : null
                }
                disabled={!isValid}
            />
            {categoryEditIndex == null ? (
                <RateEditBody
                    componentsByName={componentsByName}
                    billingCategoryRows={billingCategoryRows}
                    onEditCategory={handleEditCategory}
                    onRegisterRemove={(fn) => { removeCategoryFnRef.current = fn; }}
                    triggerValidationOnMount={!isAdding}
                />
            ) : (
                <CategoryRateEditBody
                    index={categoryEditIndex}
                    componentsByName={componentsByName}
                    billingCategoryRows={billingCategoryRows}
                    defaultLockDate={defaultLockDate}
                    triggerValidationOnMount={!isAdding}
                />
            )}
        </FormProvider>
    );
};

export default RateEditForm;
